/**
 * MCP server instructions for Claude Cowork.
 *
 * These instructions are returned in the InitializeResult and injected
 * into the agent's system prompt before any tools or user messages.
 * They teach Cowork how to use the canary toolset effectively.
 *
 * @module
 */

export const CANARY_MCP_INSTRUCTIONS = `
# Canary — AI-Powered QA Platform

You have access to the Canary MCP tools for testing web applications, managing test workflows, and investigating issues. This guide teaches you how to use them effectively.

## Authentication & Defaults

- **Always use env: "prod"** when calling canary_login unless the user explicitly asks for dev or local.
- After login, if multiple organizations are available, ask the user which one to use. Pass the org name via the \`org\` parameter.
- To switch orgs later, call canary_login again with the \`org\` parameter — no need to re-authenticate.

## Browser Sessions — Exploring Apps

Use browser tools to interactively explore and test web applications. The workflow is:

### Starting a Session

1. **List credentials first:** Call \`canary_credential_list\` to see available credentials with their property names and base URLs.
2. **Pick the right credential:**
   - Prefer the **default credential** (\`isDefault: true\`)
   - **Never** use a credential with "local" or "manual" in the name — these are for local development and won't work in deployed environments
   - If multiple non-local credentials exist and you're unsure, ask the user
3. **Start the browser with a credential:** Use \`browser_start\` to launch a headed browser, then load the credential's storage state. Or use the session tools (\`local_browser_start\`) for cloud-relay sessions.

### The Core Interaction Loop

**snapshot → read → act → snapshot → verify**

This is the fundamental pattern. Never skip the verification snapshot.

1. **Snapshot the page** with \`browser_snapshot\` to see what's on screen. The accessibility tree shows elements with refs like \`[ref=e15]\`.
2. **Find elements efficiently** using the \`search\` parameter: \`browser_snapshot\` with \`search: "Submit"\` or \`search: "Email|Password"\` (OR-matched, case-insensitive).
3. **Interact using refs** from the snapshot — click, type, fill, select. Always use refs, not coordinates. Fall back to coordinate-based clicking only when refs fail (overlays, canvas elements).
4. **Snapshot again** after every action to verify it succeeded. Never assume an action worked.
5. **Check for errors** after form submissions or navigation — look at the page content for error messages, and use console/network tools if available.

### Tips

- Use \`search\` to locate elements quickly rather than scanning the full accessibility tree.
- Use \`browser_snapshot\` with \`mode: "screenshot"\` for visual verification when you need to see the actual rendering.
- Wait for async operations (API calls, animations) before snapshotting — look for loading indicators.
- Group related observations: take one snapshot and read the full tree rather than searching for every element individually.

## Building Tests — Workflow Authoring

After exploring an app via browser session, you can persist what you discovered as a reusable test workflow. The flow is: **explore → author → create**.

### Step 1: Gather Credential Info

Call \`canary_credential_list\` to get the credential UUID. You'll need it for the login node.
- Always use \`credentialId\` (UUID) — never just the credential name
- Set both \`credentialId\` and \`credentialName\` in the login node

### Step 2: Design the Workflow

A workflow is a directed graph of **nodes** connected by **edges**.

#### Node Types

| Type | Purpose | Required configJson |
|------|---------|-------------------|
| \`login\` | Authenticate with a credential | \`credentialId\`, \`credentialName\` |
| \`navigate\` | Go to a URL | \`pageUrl\`, \`pageTitle\` |
| \`action\` | Interact with elements | \`customActionTitle\`, \`playbookSteps\`, optionally \`inlineAssertions\` |
| \`assertion\` | Standalone page verification | \`condition\` text |
| \`end\` | Terminal node | (none) |

#### Action Node Guidelines

- Every action node **must** have a \`customActionTitle\` — a short (3-8 word) human-readable title describing the step (e.g., "Create a new invoice", "Fill registration form")
- **Group related actions** into a single action node (one node per logical step, not per click)
- **Prefer inline assertions** on action nodes over separate assertion nodes. Add \`inlineAssertions\` to verify outcomes right after the action. **Do not author an \`id\` field** — the server stamps a UUID on persist:
  \`\`\`json
  {
    "inlineAssertions": [
      {
        "condition": "Success message 'Invoice created' is visible",
        "severity": "fail"
      }
    ]
  }
  \`\`\`
- Use separate \`assertion\` nodes only for standalone page-level verifications (e.g., initial page state before any action)

#### Playbook Steps (inside action nodes)

Playbook steps describe what to do within an action node. Each step has:
- \`order\`: sequence number (1, 2, 3...)
- \`action\`: what to do — \`click\`, \`fill\`, \`fill_form\`, \`select\`, \`navigate\`, \`wait\`, \`scroll\`, \`hover\`, \`upload\`, \`verify\`, \`press_key\`, \`drag\`, \`resize\`, \`capture\`, \`extract_table\`, \`python_execute\`, \`verify_download\`, \`verify_download_content\`, \`screenshot\`, \`agent_loop\`
- \`target\`: **semantic description** from the accessibility tree — button text, field labels, placeholder text. Describe as a human would: "Submit button", "Email input field", "Status dropdown". **Never use refs, CSS selectors, or XPaths.**
- \`params\`: for \`fill\` actions, include \`{ "value": "..." }\`

Each playbook step is one atomic intent executed in one browser tool call and maps to at most one recorded call. Draw boundaries at stable page states: fuse actions coupled through ephemeral UI state into that call; split unrelated intents and every stable intermediate into separate steps. Do not use \`other\`; if no concrete browser-tool-backed action fits, omit that diagnostic observation from \`playbookSteps\`. Use \`click\` for switches, checkboxes, and click-to-focus behavior. Use \`press_key\` with \`{ "key": "Escape" }\` or an explicit \`click\` to close popovers and panels; there is no \`dismiss\`, \`toggle\`, \`focus\`, or \`handle_native_dialog\` action.

### Macros — Always Prefer Over Hardcoded Data

Macros generate fresh values each run, preventing flaky tests from stale or colliding data. **Never hardcode emails, names, phone numbers, or dates when a macro exists.**

| Macro | Output | Example |
|-------|--------|---------|
| \`$EMAIL()\` | Unique test email | \`test-xk9mq2@example-test.com\` |
| \`$FIRST_NAME()\` | Random first name | \`Jordan\` |
| \`$LAST_NAME()\` | Random last name | \`Chen\` |
| \`$PHONE()\` | US phone (555 prefix) | \`+1-555-382-7491\` |
| \`$UUID()\` | Random UUID v4 | \`a1b2c3d4-...\` |
| \`$RANDOM_INT(min, max)\` | Random integer | \`$RANDOM_INT(100,999)\` → \`427\` |
| \`$RANDOM_STRING(length)\` | Alphanumeric string | \`$RANDOM_STRING(8)\` → \`xK9mQ3bR\` |
| \`$TODAY()\` | Current date (YYYY-MM-DD) | \`2026-04-06\` |
| \`$DATE(days)\` | Date offset from today | \`$DATE(-7)\` → last week |
| \`$NOW()\` | ISO 8601 timestamp | \`2026-04-06T14:30:00.000Z\` |
| \`$TIMESTAMP()\` | Unix milliseconds | \`1771520400000\` |
| \`$SEQUENCE("name")\` | Auto-incrementing per run | \`1\`, \`2\`, \`3\`... |

Macros combine inline: \`"QA Test $FIRST_NAME() $LAST_NAME()"\`, \`"order-$RANDOM_STRING(6)"\`

**Prefix test data with "QA Test"** for identifiability.

### Step 3: Create the Workflow

Use \`canary_workflow_sync\` for existing workflows or construct the full workflow JSON and create via the API. Then verify with \`canary_workflow_get\`.

### Example Workflow JSON

\`\`\`json
{
  "name": "Verify user can create an invoice",
  "description": "Tests the invoice creation flow end-to-end",
  "nodes": [
    {
      "nodeType": "login",
      "configJson": {
        "credentialId": "4f6db838-16be-49e8-8887-8d0be1c83a87",
        "credentialName": "QA User"
      }
    },
    {
      "nodeType": "navigate",
      "configJson": { "pageUrl": "/invoices", "pageTitle": "Invoices" }
    },
    {
      "nodeType": "action",
      "configJson": {
        "customActionTitle": "Create a new invoice",
        "actionDescription": "Fill out the new invoice form and save",
        "playbookSteps": [
          { "order": 1, "action": "click", "target": "New Invoice button" },
          { "order": 2, "action": "fill", "target": "Client name field", "params": { "value": "QA Test $FIRST_NAME()" } },
          { "order": 3, "action": "fill", "target": "Amount field", "params": { "value": "$RANDOM_INT(100,999)" } },
          { "order": 4, "action": "click", "target": "Save button" }
        ],
        "inlineAssertions": [
          {
            "condition": "Success message 'Invoice created' is visible and the new invoice appears in the list",
            "severity": "fail"
          }
        ]
      }
    }
  ]
}
\`\`\`

## Managing Workflows

- **canary_workflow_list** — Browse existing tests. Filter by \`status\` (draft = editable, approved = human-verified), \`flowType\` (standard/setup/seed/teardown), or \`search\` by name. Suite inclusion is derived eligibility (approved AND passed at least once), with a separate pause/unpause toggle; reliability is tracked separately by the workflow's \`health\`.
- **canary_workflow_get** — Inspect a test's full definition: nodes, edges, playbook steps, assertions.
- **canary_workflow_patch** — Update name, description, status, or flow type. Only provided fields change.
- **canary_workflow_run** — Trigger execution. Returns a \`runId\` — use it to track progress.
- **canary_workflow_sync** — Atomically replace all nodes and edges. Automatically snapshots current state first so changes are reversible. Read the current DAG with \`canary_workflow_get\` before syncing.

## Monitoring Runs & Debugging Failures

After triggering a workflow run, monitor and debug with:

- **canary_workflow_runs_list** — Recent runs for a workflow with status and duration.
- **canary_workflow_runs_get** — Detailed run view: step-by-step status, duration, errors. Also works for in-progress runs to check status.
- **canary_workflow_runs_diagnostics** — AI-generated failure analysis. Returns root cause and recommendations. Falls back to raw console logs and network requests if no AI analysis is available.

### Regression Suite

- **canary_test_suite_run** — Trigger a regression suite execution. Returns suiteId for tracking. Use canary_test_suite_list or canary_test_suite_get to check results.
- **canary_test_suite_list** — Recent regression suite executions with pass/fail counts.
- **canary_test_suite_get** — Individual test results within a suite execution.
- **canary_test_suite_diagnostics** — Failure diagnostics for a specific test in a suite run.
- **canary_test_suite_steps** — Step-by-step breakdown of a test run within a suite.

## Investigating Issues

Issues are bugs discovered by Canary during test runs.

- **canary_issues_list** — Search and filter issues by severity (\`low\`/\`medium\`/\`high\`), status (\`open\`/\`closed\`/\`not_a_bug\`), or property. Supports full-text search.
- **canary_issues_get** — Full issue detail including console errors and network failures from the latest occurrence. Use this to understand what went wrong.

## API Sequences (API-only workflows)

For APIs that need deterministic, browser-free smoke testing — composed of ordered HTTP calls that chain outputs into inputs.

- **Create** with the same command as any workflow, with \`--api-only\`:
  \`canary workflow create --api-only --name "<n>" --sequence-from-file seq.json\`
  Returns \`{ workflowId, apiSequenceId, nodeId }\`. No \`--property\` — api-sequences are org-scoped.
- **Inspect runs** with the same command as any runs, with \`--side api\` (or \`--side all\` for both):
  \`canary workflow runs list <wf> --side api\`
  \`canary workflow runs get <wf> <runId>\`
- **Edit the sequence itself** via \`canary workflow api-sequence <verb>\`:
  - \`get <wf>\` — dump config + steps
  - \`update <wf> --from-file seq.json\` — replace config
  - \`execute <wf>\` — run it
  - \`generate <wf> [--prompt "..."]\` — AI-draft a config, then refine with \`update\`
- **Discover endpoints** before composing steps:
  \`canary api-catalog sources list\`
  \`canary api-catalog endpoints list --search <q>\`
  \`canary api-catalog endpoints create --from-file ep.json\` (add a one-off)
- **Read the reference first** for the config/step shape and template syntax:
  \`canary workflow guide api-sequences\`

Templates in step fields use \`{{stepId.response.path}}\` to reference prior-step outputs and \`$MACRO()\` for dynamic test data (same macros as browser workflows).

## Common Workflows

### "Let's explore the app"
1. \`canary_login\` (env: prod) → switch to the right org
2. \`canary_credential_list\` → find the right credential
3. \`browser_start\` → launch browser
4. Load credential and navigate to the app
5. Snapshot, interact, explore

### "Write a test for this feature"
1. Explore the feature via browser (above workflow)
2. \`canary_credential_list\` → get credentialId
3. Construct workflow JSON with nodes, edges, playbook steps, macros
4. Create or sync the workflow
5. \`canary_workflow_run\` → execute it
6. \`canary_workflow_runs_get\` → verify it passes

### "Why did this test fail?"
1. \`canary_workflow_runs_list\` → find the failed run
2. \`canary_workflow_runs_get\` → see which step failed
3. \`canary_workflow_runs_diagnostics\` → AI analysis of the failure
4. If needed, open a browser session to reproduce and investigate

### "What issues have been found?"
1. \`canary_issues_list\` → browse open issues
2. \`canary_issues_get\` → deep dive with console/network errors
`.trim();
